import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, BellRing, CalendarDays, CheckCheck, Check, X, Loader2 } from 'lucide-react';
import { useRestaurant } from '../context/RestaurantContext';
import apiClient from '../services/api';
import { useToast } from './ui/use-toast';
import { confirm } from './ui/confirm-dialog';
import { isMuted, setMuted } from '../lib/notifyMute';
import { subscribeRealtime, fallbackPoll } from '../lib/realtime';
import { useT } from '../context/LanguageContext';

// Safety-net poll; realtime carries new requests within a second.
const POLL_MS = 20000;
const READ_KEY = 'notif_read_ids';

const readIds = () => {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY)) || []); }
  catch { return new Set(); }
};
const saveReadIds = (set) => {
  // Cap the persisted list so it never grows unbounded.
  localStorage.setItem(READ_KEY, JSON.stringify([...set].slice(-300)));
};

const fmtWhen = (iso) => (iso
  ? new Date(iso).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '');

/** Header notification bell: badge and dropdown, currently fed by pending reservation requests polled every 30s. */
export default function NotificationBell() {
  const { restaurantId } = useRestaurant();
  const navigate = useNavigate();
  const { t } = useT();
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [read, setRead] = useState(readIds);
  const [decidingId, setDecidingId] = useState(null);
  /* Per-device mute silences the chime and toasts, NOT the badge - waiting work stays visible. */
  const [muted, setMutedUI] = useState(isMuted);
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedUI(next);
  };
  const boxRef = useRef(null);
  const btnRef = useRef(null);
  // The panel is pinned to the window's right edge, not the bell, so only `top` needs measuring.
  const [panelTop, setPanelTop] = useState(0);
  const { toast } = useToast();

  // Poll pending reservation requests.
  useEffect(() => {
    if (!restaurantId) return undefined;
    let stop = false;
    const check = async () => {
      try {
        const { data } = await apiClient.get(`/users/restaurant/${restaurantId}/reservations/pending/`);
        if (!stop && data?.success) setPending(data.results || []);
      } catch { /* next poll retries */ }
    };
    check();
    const unsub = subscribeRealtime(['reservations'], () => check());
    // Tightens to a few seconds by itself while the socket is not open.
    const stopPoll = fallbackPoll(check, POLL_MS);
    return () => { stop = true; unsub(); stopPoll(); };
  }, [restaurantId]);

  // Close on outside click / Escape; keep the panel just under the bell.
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setPanelTop(r.bottom + 8);
    };
    place();
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const items = useMemo(() => pending.map((r) => ({
    id: `resv-${r.id}`,
    Icon: CalendarDays,
    title: t('dashboard.notif.reservationRequest'),
    desc: `${r.customer_name || t('common.guest')} · ${r.pax || '?'} ${t('dashboard.notif.pax')}`
      + `${r.reserved_start_at ? ` · ${fmtWhen(r.reserved_start_at)}` : ''}`
      + `${r.customer_phone ? ` · ${r.customer_phone}` : ''}`
      + `${r.dining_type === 'BUFFET' ? ` · ${t('dashboard.notif.buffet')}${r.buffet_name ? `: ${r.buffet_name}` : ''}` : ''}`,
    notes: r.notes || '',
    reservation: r,
    // All Reservations, opened on the awaiting-approval filter, where requests are decided.
    to: '/reservations/all?status=BOOKED',
  })), [pending, t]);

  // Approve / decline straight from the dropdown, using the same endpoints; approve keeps the requested table.
  const decide = async (r, action) => {
    if (action === 'decline') {
      const ok = await confirm({
        title: t('dashboard.notif.declineTitle'),
        description: t('dashboard.notif.declineBody', {
          who: r.customer_name || t('common.guest'),
          pax: r.pax || '?',
        }),
        confirmText: t('dashboard.notif.declineConfirm'),
        tone: 'destructive',
      });
      if (!ok) return;
    }
    setDecidingId(r.id);
    try {
      const { data } = await apiClient.post(
        `/users/restaurant/${restaurantId}/reservations/${r.id}/${action}/`, {});
      if (!data?.success) throw new Error(data?.error || t('dashboard.notif.failed'));
      toast({
        title: action === 'approve' ? t('dashboard.notif.approved') : t('dashboard.notif.declined'),
        description: `${r.customer_name || t('common.guest')} · ${r.pax || '?'} ${t('dashboard.notif.pax')}`,
      });
      markRead([`resv-${r.id}`]);
      setPending((prev) => prev.filter((p) => p.id !== r.id));
    } catch (e) {
      toast({ title: t('dashboard.notif.error'), description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setDecidingId(null);
    }
  };

  const unread = items.filter((it) => !read.has(it.id));

  const markRead = useCallback((ids) => {
    setRead((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      saveReadIds(next);
      return next;
    });
  }, []);

  const openItem = (it) => {
    markRead([it.id]);
    setOpen(false);
    navigate(it.to);
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`${t('dashboard.notif.title')}${muted ? ` ${t('dashboard.notif.mutedSuffix')}` : ''}${unread.length ? ` ${t('dashboard.notif.unreadSuffix', { count: unread.length })}` : ''}`}
        title={muted ? t('dashboard.notif.bellTitleMuted') : t('dashboard.notif.title')}
        className="relative p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      >
        {muted ? <BellOff size={18} className="opacity-70" />
          : unread.length > 0 ? <BellRing size={18} className="text-amber-600" /> : <Bell size={18} />}
        {unread.length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{ top: panelTop }}
          className="fixed right-4 w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card shadow-lg z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border">
            <span className="text-sm font-semibold text-foreground">{t('dashboard.notif.title')}</span>
            <span className="flex items-center gap-3">
              {unread.length > 0 && (
                <button
                  type="button"
                  onClick={() => markRead(items.map((it) => it.id))}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <CheckCheck size={13} /> {t('dashboard.notif.markAllRead')}
                </button>
              )}
              {/* Mute quiets the chime and toasts on THIS device; the badge keeps counting. */}
              <button
                type="button"
                onClick={toggleMute}
                title={muted
                  ? t('dashboard.notif.muteOnTitle')
                  : t('dashboard.notif.muteOffTitle')}
                className={`inline-flex items-center gap-1 text-xs ${
                  muted ? 'text-amber-700 font-medium' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {muted
                  ? <><Bell size={13} /> {t('dashboard.notif.unmute')}</>
                  : <><BellOff size={13} /> {t('dashboard.notif.mute')}</>}
              </button>
            </span>
          </div>
          {muted && (
            <div className="px-4 py-2 border-b border-border bg-amber-50/60 text-[11px] text-amber-800">
              {t('dashboard.notif.mutedBanner')}
            </div>
          )}
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                All caught up — nothing needs your attention.
              </div>
            ) : items.map((it) => {
              const isUnread = !read.has(it.id);
              const busy = decidingId === it.reservation?.id;
              return (
                <div
                  key={it.id}
                  className={`px-4 py-3 border-b border-border last:border-b-0 transition-colors ${
                    isUnread ? 'bg-amber-50/60 dark:bg-amber-500/10' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => openItem(it)}
                    className="w-full text-left flex items-start gap-3 hover:opacity-80"
                  >
                    <it.Icon size={16} className={`mt-0.5 shrink-0 ${isUnread ? 'text-amber-600' : 'text-muted-foreground'}`} />
                    <span className="min-w-0">
                      <span className={`block text-sm ${isUnread ? 'font-semibold text-foreground' : 'text-foreground/80'}`}>
                        {it.title}
                      </span>
                      <span className="block text-xs text-muted-foreground">{it.desc}</span>
                      {it.notes && (
                        <span className="block text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                          “{it.notes}”
                        </span>
                      )}
                    </span>
                    {isUnread && <span className="ml-auto mt-1.5 h-2 w-2 rounded-full bg-red-500 shrink-0" />}
                  </button>
                  {it.reservation && (
                    <div className="mt-2 flex items-center gap-2 pl-7">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => decide(it.reservation, 'approve')}
                        title={it.reservation.table_code ? `Approve — keeps table ${it.reservation.table_code}` : 'Approve this booking'}
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-2.5 py-1 disabled:opacity-60"
                      >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Approve
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => decide(it.reservation, 'decline')}
                        className="inline-flex items-center gap-1 rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 text-xs font-medium px-2.5 py-1 disabled:opacity-60"
                      >
                        <X size={12} /> Reject
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
